import { computed, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import { useProjectContext } from "./useProjectContext";
import type {
  ProjectStatus,
  ClientInboxLeadGroup,
  ExpertInboxProject,
  ProjectStatusTab,
} from "@/v2Projects/types";

const LEAD_STATUSES: ProjectStatus[] = ["pending"];
const EXPERT_ACTIVE_STATUSES: ProjectStatus[] = ["quoted", "in_progress", "submitted"];
const CLIENT_ACTIVE_STATUSES: ProjectStatus[] = ["pending", "quoted", "in_progress", "submitted"];
const CLOSED_STATUSES: ProjectStatus[] = ["completed", "cancelled", "rejected"];

interface UseProjectsListOptions {
  limit?: number;
  fetchAllPages?: boolean;
}

export function useProjectsList(options: UseProjectsListOptions = {}) {
  const { side } = useProjectContext();
  const api = useApi();
  const activeTab = ref<ProjectStatusTab>(side.value === "expert" ? "leads" : "all");
  const limit = options.limit ?? 50;
  const fetchAllPages = options.fetchAllPages ?? false;

  const statusFilter = computed<ProjectStatus[] | undefined>(() => {
    if (activeTab.value === "leads") return LEAD_STATUSES;
    if (activeTab.value === "active") {
      return side.value === "expert" ? EXPERT_ACTIVE_STATUSES : CLIENT_ACTIVE_STATUSES;
    }
    if (activeTab.value === "closed") return CLOSED_STATUSES;
    return undefined;
  });

  const query = useQuery({
    queryKey: computed(() => [
      "v2projects",
      "inbox",
      side.value,
      activeTab.value,
      limit,
      fetchAllPages,
    ]),
    queryFn: async () => {
      const fetchPage = async (cursor?: string) => {
        const q: { limit?: number; cursor?: string; status?: ProjectStatus[] } = { limit };
        if (cursor) q.cursor = cursor;
        if (statusFilter.value) q.status = statusFilter.value;

        if (side.value === "expert") {
          return api.expert.projectsV2.listInboxProjects({ query: q as never });
        }
        return api.client.projectsV2.listInboxProjects({ query: q as never });
      };

      const firstPage = await fetchPage();
      if (!fetchAllPages) return firstPage;

      const data: unknown[] = [...firstPage.data];
      let pagination = firstPage.pagination;
      const seenCursors = new Set<string>();

      while (pagination.has_next && pagination.cursor && !seenCursors.has(pagination.cursor)) {
        seenCursors.add(pagination.cursor);
        const nextPage = await fetchPage(pagination.cursor);
        data.push(...nextPage.data);
        pagination = nextPage.pagination;
      }

      return { ...firstPage, data, pagination };
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const clientGroups = computed<ClientInboxLeadGroup[]>(() => {
    if (side.value !== "client") return [];
    return (query.data.value?.data ?? []) as ClientInboxLeadGroup[];
  });

  const expertProjects = computed<ExpertInboxProject[]>(() => {
    if (side.value !== "expert") return [];
    return (query.data.value?.data ?? []) as ExpertInboxProject[];
  });

  const totalCount = computed(() => {
    if (side.value === "client") return clientGroups.value.length;
    return expertProjects.value.length;
  });

  const hasMore = computed(() => query.data.value?.pagination?.has_next ?? false);

  return {
    side,
    activeTab,
    clientGroups,
    expertProjects,
    totalCount,
    hasMore,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
